import {
  Alert,
  Autocomplete,
  Button,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import { ReactComponent as CloseIcon } from "assets/images/CloseIcon.svg";
import ErrorIcon from "assets/images/warning-2.svg";
import { CircularLoaderFullPage, DatePickerField } from "components/core";

import { ReactComponent as ArrowDown } from "assets/images/ArrowDown.svg";
import CommonModal from "components/shared/common-modal/CommonModal";
import dayjs from "dayjs";
import { t } from "i18next";
import { useState } from "react";
import { useSelector } from "react-redux";
import { selectAuth } from "store/slices";

import { useEffect } from "react";
import { CF_URLS } from "services/api/endpoints";
import {
  CLEAR,
  dateFormat,
  ERROR_TYPES,
  VALUE_EMPTY_STRING
} from "utils/constants";
import {
  dataDogErrorLog,
  findErrorMessage,
  getAuthenticatedUserBranch,
  handleRefId
} from "utils/helpers";
import { getRunDates, listRunsByDate } from "../Services";

const RunArrival = ({
  isOpen,
  onCloseModal,
  setCommonDownloadModal,
  getCallbackData,
  setReportCFUrl
}) => {
  const { localeByBranch, currentBranch } = useSelector(selectAuth);
  const mainDistrictId = String(
    getAuthenticatedUserBranch() ||
      currentBranch?.district_id ||
      VALUE_EMPTY_STRING
  );
  const [throwErrorMessage, setThrowErrorMessage] = useState("");
  const [throwError, setThrowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const today = dayjs();
  const defaultValue = today;
  const [selectedServiceDate, setSelectedServiceDate] = useState(defaultValue);
  const [selectedOption, setSelectedOption] = useState(null);
  const [getRunDate, setGetRunDate] = useState(null);
  const [isDisable, setIsDisable] = useState(true);
  const [runList, setRunList] = useState([]);

  // print button functionality
  const handlePrint = () => {
    const serviceDate = dayjs(selectedServiceDate).format(
      dateFormat.universalFormat
    );

    const propData = {
      serviceDate: serviceDate,
      run: selectedOption
    };
    getCallbackData(propData);
    setReportCFUrl(CF_URLS.printReports.reports.runArrivalReport);
    setCommonDownloadModal(true);
  };
  const handleDateChange = (date) => {
    setSelectedServiceDate(date);
    setSelectedOption(null);
    setIsDisable(true);
  };
  const handleRunChange = (event, newValue, reason) => {
    if (reason === CLEAR) {
      setIsDisable(true);
      setSelectedOption(null);
    }
    if (newValue) {
      setSelectedOption(newValue);
      setIsDisable(false);
    }
  };
  const getDistriButionReunDate = async () => {
    try {
      setIsLoading(true);
      let res = await getRunDates(mainDistrictId);
      if (res) {
        setGetRunDate(res.data[0][0]);
      }
      setIsLoading(false);
    } catch (error) {
      const { refId, message } = handleRefId(
        findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(message, "", { error }, refId);
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getDistriButionReunDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getRunsList = async () => {
    const dateFormate = dayjs(selectedServiceDate).format(
      dateFormat.universalFormat
    );
    try {
      setIsLoading(true);
      let res = await listRunsByDate(mainDistrictId, dateFormate);
      if (res) {
        setRunList(res.data[0]);
      }
      setIsLoading(false);
    } catch (error) {
      const { refId, message } = handleRefId(
        findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(message, "", { error }, refId);
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getRunsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceDate]);
  return (
    <>
      <CommonModal
        open={isOpen}
        minWidth={600}
        title={t("reports.runArrival.title")}
        body={
          <>
            {isLoading && <CircularLoaderFullPage loading={isLoading} />}
            {throwError && (
              <Collapse in={throwError}>
                <Alert
                  severity="error"
                  icon={<img src={ErrorIcon} alt="error" />}
                  action={
                    <IconButton
                      aria-label={t("common.close")}
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setThrowError(false);
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {throwErrorMessage}
                </Alert>
              </Collapse>
            )}
            <Typography>{t("reports.runArrival.runInformation")}</Typography>
            <br />
            <Grid container spacing={4}>
              <Grid item lg={6} md={6} xs={12}>
                <FormControl fullWidth>
                  <DatePickerField
                    name="serviceDate"
                    label={t("reports.runArrival.serviceDate")}
                    value={dayjs(selectedServiceDate)}
                    minDate={
                      getRunDate ? dayjs(getRunDate.oldest_run_date) : today
                    }
                    maxDate={
                      getRunDate ? dayjs(getRunDate.newest_run_date) : today
                    }
                    defaultValue={defaultValue}
                    onChange={handleDateChange}
                    locale={localeByBranch}
                  />
                </FormControl>
              </Grid>
              <Grid item lg={6} md={6} xs={12}>
                <Autocomplete
                  componentsProps={{
                    clearIndicator: ArrowDown
                  }}
                  popupIcon={<ArrowDown />}
                  clearIcon={<CloseIcon />}
                  name={t("reports.runArrival.run")}
                  disabled={null}
                  options={runList || []}
                  getOptionLabel={(option) => option.run_name}
                  size="medium"
                  value={selectedOption}
                  onChange={handleRunChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("reports.runArrival.run")}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        }
        buttons={
          <>
            <Button variant="outlined" onClick={() => onCloseModal(false)}>
              {t("common.close")}
            </Button>
            <Button
              variant="contained"
              onClick={handlePrint}
              disabled={isDisable}
            >
              {t("common.print")}
            </Button>
          </>
        }
      />
    </>
  );
};

export default RunArrival;
